import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { formatLinkBlockToUrl } from '$utilities/data.js';
import { GridBlock } from '$partials/grid/layout/index.js';
import { SimpleImage as Image } from '$partials/media/index.js';

/* ========================================================================= */
/* Configuration */
/* ========================================================================= */

export const paddingSizes = {
    1: '30',
    2: '40',
    3: '50',
    4: '60'
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const GridAssetBlock = (props) => {
    const {
        assetDefinesMobileHeight = true,
        caption,
        className,
        height = 1,
        image,
        link,
        padding = '1111',
        relativeUrl,
        video,
        width = 1,
    } = props;
    const [containerDimensions, setContainerDimensions] = useState([width, height]);
    const [videoDimensions, setVideoDimensions] = useState([]);
    const containerRef = useRef();
    const videoRef = useRef();

    let didResize = false;

    useEffect(() => {

        const measureContainerDimensions = () => {
            const rect = containerRef.current.getBoundingClientRect();
            setContainerDimensions([rect.width, rect.height]);
        };

        const measureVideoDimensions = () => {
            if (videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
                setVideoDimensions([videoRef.current.videoWidth, videoRef.current.videoHeight]);
            }
        };

        const handleResize = () => {
            if (!didResize) {
                window.requestAnimationFrame(() => {
                    measureContainerDimensions();
                    didResize = false;
                });
                didResize = true;
            }
        };

        if (containerRef.current) {
            measureContainerDimensions();
            window.addEventListener('resize', handleResize);
        }

        if (videoRef.current) {
            measureVideoDimensions();
            videoRef.current.addEventListener('loadedmetadata', measureVideoDimensions);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            if (videoRef.current) {
                videoRef.current.removeEventListener('loadedmetadata', measureVideoDimensions);
            }
        };
    }, []);

    const assetClassName = props.id ? encodeURIComponent(props.id).toLowerCase().replace(/\.|%[0-9a-z]{2}/gi, '') : '';

    if (!image && !video) { return null; }

    // If the block is captioned, only 1 cell high and the image has a portrait aspect ratio, switch to horizontal layout
    const isHorizontal = (caption && height === 1 && (image?.width / image?.height) < (3 / 2)) ? true : false;

    // Content padding is derived from the smallest cell dimension
    const paddingSize = paddingSizes[Math.min(width, height)];
    const paddingConfig = padding.split('').map(v => Number(v));
    const noPadding = paddingConfig.every((v) => v === 0);

    const assetRatio = video ? videoDimensions.length > 0 ? videoDimensions[1] / videoDimensions[0] : 1 : image ? image.height / image.width : 1;
    const containerRatio = containerDimensions[1] / containerDimensions[0];
    const assetWidth = assetRatio > containerRatio ? (containerRatio / assetRatio) * 100 : 100;
    const assetHeight = assetRatio > containerRatio ? 100 : (assetRatio / containerRatio) * 100;

    // CSS

    let outerContainerClasses = 'h-full w-full min-w-[0] min-h-[0]';
    let containerClasses = 'h-full w-full flex';
    let assetClasses = `${assetClassName} object-cover`;

    // Position
    let objectX, objectY;
    if (paddingConfig[0] === 0 && paddingConfig[2] === 1) { objectY = 'start'; }
    if (paddingConfig[1] === 0 && paddingConfig[3] === 1) { objectX = 'end'; }
    if (paddingConfig[2] === 0 && paddingConfig[0] === 1) { objectY = 'end'; }
    if (paddingConfig[3] === 0 && paddingConfig[1] === 1) { objectX = 'start'; }
    if (objectX) {
        containerClasses += ` justify-${objectX}`;
    } else {
        containerClasses += ` ${isHorizontal ? 'justify-center sm:justify-end' : 'justify-center'}`;
    }
    if (objectY) {
        containerClasses += ` items-${objectY}`;
    } else {
        containerClasses += ' items-center';
    }

    // Padding
    if (!noPadding) {
        const mobileYPadding = 'layout-item';
        if (paddingConfig[0] === 1) { outerContainerClasses += ` pt-${mobileYPadding} sm:pt-${paddingSize}`; }
        if (paddingConfig[1] === 1) { outerContainerClasses += ` pr-layout-item sm:pr-${paddingSize}`; }
        if (paddingConfig[2] === 1) {
            outerContainerClasses += ` pb-${mobileYPadding} sm:pb-${paddingSize}`;
        } else if (caption) {
            outerContainerClasses += ` pb-${mobileYPadding} sm:pb-0`;
        }
        if (paddingConfig[3] === 1) { outerContainerClasses += ` pl-layout-item sm:pl-${paddingSize}`; }
    }

    // Rounded corners
    if (!noPadding) {
        if (paddingConfig[0] === 1 && paddingConfig[1] === 1) { assetClasses += ' rounded-tr-xl'; }
        if (paddingConfig[0] === 1 && paddingConfig[3] === 1) { assetClasses += ' rounded-tl-xl'; }
        if ((paddingConfig[2] === 1 && paddingConfig[1] === 1) || (caption && paddingConfig[1] === 1)) { assetClasses += ' rounded-br-xl'; }
        if ((paddingConfig[2] === 1 && paddingConfig[3] === 1) || (caption && paddingConfig[3] === 1)) { assetClasses += ' rounded-bl-xl'; }
    }

    // Hide video until dimensions are determined
    if (video && videoDimensions.length === 0) {  assetClasses += ' opacity-0'; }

    // Caption padding
    let captionPadding = noPadding ?
        `p-layout-item sm:p-${paddingSize}` :
        `pb-layout-item sm:pb-${paddingSize} pl-layout-item sm:pl-${paddingSize} ${isHorizontal ?
            `sm:pt-${paddingSize}` :
            `pr-layout-item sm:pr-${paddingSize}`}`;

    const layout = (
        <figure className={`h-full w-full flex flex-col ${isHorizontal ? 'sm:flex-row-reverse sm:items-end' : ''} ${className || ''}`}>
            <div className={outerContainerClasses}>
                <div
                    className={containerClasses}
                    ref={containerRef}>
                    <style dangerouslySetInnerHTML={{ __html: `
                        ${assetDefinesMobileHeight ? `
                            .${assetClassName} {
                                width: 100%;
                            }
                            @media (min-width: 760px) {
                                .${assetClassName} {
                                    width: ${noPadding ? '100' : assetWidth}%;
                                    height: ${noPadding ? '100' : assetHeight}%;
                                }
                        }` : `
                            .${assetClassName} {
                                width: ${noPadding ? '100' : assetWidth}%;
                                height: ${noPadding ? '100' : assetHeight}%;
                            }
                        `}
                    `}} />

                    {(image && !video) &&
                        <Image
                            {...image}
                            className={assetClasses} />
                    }

                    {video &&
                        <video
                            autoPlay
                            className={assetClasses}
                            controls={false}
                            loop={true}
                            muted={true}
                            playsInline={true}
                            ref={videoRef}
                            src={video.url}
                            style={{ transition: 'opacity .3s' }} />
                    }
                </div>
            </div>

            {caption &&
                <figcaption
                    className={`max-width-[20em] ${captionPadding}`}
                    dangerouslySetInnerHTML={{ __html: caption }} />
            }
        </figure>
    );

    return (
        <GridBlock {...props}>
            {link ? (
                <a href={formatLinkBlockToUrl((link))}>
                    {layout}
                </a>
            ) : relativeUrl ? (
                <a href={relativeUrl}>
                    {layout}
                </a>
            ) : layout}
        </GridBlock>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    assetDefinesMobileHeight: PropTypes.bool,
    caption: PropTypes.string,
    className: PropTypes.string,
    height: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
    image: PropTypes.object,
    link: PropTypes.object,
    padding: PropTypes.string.isRequired,
    video: PropTypes.shape({
        url: PropTypes.string.isRequired
    }),
    relativeUrl: PropTypes.string,
    width: PropTypes.number.isRequired,
};

GridAssetBlock.propTypes = propTypes;

export default GridAssetBlock;
